import type { FunctionComponent } from 'react';
import { useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { PageContainer } from '../../../components/PageContainer/PageContainer';
import {
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
  radiusHelper,
  boxShadow,
} from '@property.com.au/ui-kit';
import type { GeoJsonData } from '../../../app-kit/components/PcaMap/PcaMap';
import { PcaMap } from '../../../app-kit/components/PcaMap/PcaMap';
import { isNonEmptyArray } from '../../../app-kit/helpers/arrayHelpers';
import { onPropertyMapLoaded } from './helpers/onPropertyMapLoaded';
import { OutboundLink } from '../../../app-kit/components/OutboundLink/OutboundLink';
import type { LabelWithCoordinates, Point } from '../../../app-kit/components/PcaMap/types';
import type { MapInteractionEventData } from '../../../analytics/event-tracking/trackMapInteraction';
import { MapTypeSwitcherWithData } from './subcomponents/MapTypeSwitcher/MapTypeSwitcher';
import { PcaMapContextProvider } from '../../contexts/PcaMapContext/PcaMapContextProvider';
import { mapPinToMarkerFactory } from '../../../app-kit/components/PcaMap/helpers/mapPinToMarkerFactory';
import { ZoomButtons } from '../../../app-kit/components/PcaMap/subcomponents/ZoomButtons/ZoomButtons';
import { FullScreenButton } from '../../../app-kit/components/PcaMap/subcomponents/FullScreenButton/FullScreenButton';
import { PropertyBoundaryMeasurementLabels } from './subcomponents/PropertyBoundaryMeasurementLabels/PropertyBoundaryMeasurementLabels';
import { PropertyBoundaryLandAreaLabels } from './subcomponents/PropertyBoundaryLandAreaLabels/PropertyBoundaryLandAreaLabels';
import { PropertyBoundaryVertices } from './subcomponents/PropertyBoundaryVertices/PropertyBoundaryVertices';

const PROPERTY_MAP_HEIGHT = '346px';
const PROPERTY_MAP_HEIGHT_WITH_BOUNDARIES = '560px';
const INITIAL_ZOOM_LEVEL = 17;

const MapTypeSwitcherContainer = styled.div`
  margin-bottom: ${spacingHelper('medium')};
`;

const PropertyMapContainer = styled(PageContainer)<{ $hasBoundaries: boolean }>`
  position: relative;
  height: ${(props) =>
    props.$hasBoundaries ? PROPERTY_MAP_HEIGHT_WITH_BOUNDARIES : PROPERTY_MAP_HEIGHT};
  ${boxShadow.defaultBoxShadow};
  background: ${paletteHelper('backgroundPrimaryHigh')};
  border-radius: ${radiusHelper('medium')};
  &:not(:first-of-type) {
    margin-top: ${spacingHelper('extraSmall')};
  }
  .gm-style {
    .control-buttons {
      display: block;
    }
  }
`;

const CopyrightText = styled(Text)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  padding-top: ${spacingHelper('extraSmall')};
`;

const CopyrightLink = styled(OutboundLink)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  text-decoration: underline;
  &:hover,
  &:focus,
  &:focus-within {
    color: ${paletteHelper('textPrimary')};
  }
`;
interface MapInteractionTracking {
  changeZoomLevel: MapInteractionEventData;
  toggleFullscreen: MapInteractionEventData;
}
interface PropertyMapBrickProps {
  title: string;
  subtitle?: string | null;
  pins?: {
    latitude: number;
    longitude: number;
  }[];
  tracking?: MapInteractionTracking | null;
  propertyBoundary?: GeoJsonData | null;
  buildingBoundaries?: GeoJsonData | null;
  propertyBoundaryMeasurements?: LabelWithCoordinates[] | null;
  propertyBoundaryLandAreas?: LabelWithCoordinates[] | null;
  propertyBoundaryVertices?: Point[] | null;
}
const ControlButtonsContainer = styled.div`
  display: none;
  transition: 0.2s;
  margin-top: ${spacingHelper('small')};
  margin-right: ${spacingHelper('small')};
  > button {
    margin-top: ${spacingHelper('small')};
  }
`;

export const PropertyMapBrick: FunctionComponent<PropertyMapBrickProps> = ({
  pins,
  tracking,
  propertyBoundary,
  buildingBoundaries,
  propertyBoundaryMeasurements,
  propertyBoundaryLandAreas,
  propertyBoundaryVertices,
}) => {
  const theme = useTheme();
  const controlButtonsRef = useRef<HTMLDivElement>(null);
  if (!pins || !isNonEmptyArray(pins)) return null;
  const { latitude: lat, longitude: lng } = pins[0];
  const hasBoundaries = Boolean(propertyBoundary || buildingBoundaries);

  return (
    <>
      <PcaMapContextProvider
        {...(hasBoundaries
          ? {}
          : { zoom: INITIAL_ZOOM_LEVEL, markerFactories: pins.map(mapPinToMarkerFactory) })}
        center={{ lat, lng }}
        onMapLoaded={onPropertyMapLoaded({
          tracking,
          propertyBoundary,
          buildingBoundaries,
          controlButtonsRef,
          theme,
        })}
        outboundLinkType="google-map-link-to-property-location"
        zoomControl={false}
        fullscreenControl={false}
      >
        <MapTypeSwitcherContainer>
          <MapTypeSwitcherWithData />
        </MapTypeSwitcherContainer>
        <PropertyMapContainer
          as="section"
          data-testid="property-map-container"
          $hasBoundaries={hasBoundaries}
        >
          <PcaMap />
          <PropertyBoundaryVertices propertyBoundaryVertices={propertyBoundaryVertices} />
          <PropertyBoundaryMeasurementLabels
            propertyBoundaryMeasurements={propertyBoundaryMeasurements}
          />
          <PropertyBoundaryLandAreaLabels propertyBoundaryLandAreas={propertyBoundaryLandAreas} />
          <ControlButtonsContainer ref={controlButtonsRef} className="control-buttons ">
            <ZoomButtons />
            <FullScreenButton />
          </ControlButtonsContainer>
        </PropertyMapContainer>
      </PcaMapContextProvider>
      {hasBoundaries && (
        <CopyrightText>
          Measurements are estimates only. Data provided under licence from &copy; Geoscape
          Australia 2022.{' '}
          <CopyrightLink
            href="https://geoscape.com.au/legal/data-copyright-and-disclaimer/"
            outboundLinkType="property-map-disclaimer"
            rel="noopener noreferrer"
          >
            Copyright and Disclaimer Notice.
          </CopyrightLink>
        </CopyrightText>
      )}
    </>
  );
};
